import {
  ArrowRightOutlined,
  CheckOutlined,
  PlusOutlined,
  ReadOutlined,
  RetweetOutlined,
  SwapRightOutlined,
  WarningOutlined,
} from '@ant-design/icons';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { RotationCandidate, RotationMatch, RotationSwap, SwapSide } from '@shared/types';
import { api } from '../api/client.js';
import type { ExportTable } from '../lib/exportTable.js';
import {
  addToDraft,
  clauseLabel,
  draftIssues,
  durationTH,
  inDraft,
  isLevelDown,
  itemKey,
  leafUnit,
  personProposed,
  QUICK_REASONS,
  removeFromDraft,
  setConsent,
  setReason as setDraftReason,
  shortlistOf,
  summarise,
  targetInDraft,
  toReviewItems,
  TRANSFER_CLAUSES,
  useDraft,
  type ClauseId,
  type DraftItem,
} from '../lib/rotationDraft.js';
import { levelLabel } from '../pages/PersonCard.js';
import { CandidateBoard } from './CandidateBoard.js';
import { ExportButtons } from './ExportButtons.js';
import { exportRotationMemo } from '../lib/rotationMemo.js';

/**
 * แผนหมุนเวียนกำลังคน — สองทางที่ทำได้จริง แยกเป็นสองแท็บ
 *
 * ────────────────────────────────────────────────────────────────
 *  ทำไมต้องมีสองแท็บ ไม่ใช่รายการเดียว
 * ────────────────────────────────────────────────────────────────
 *
 *   เติมอัตราว่าง  ย้ายคนเข้าอัตราที่ว่างอยู่ — ต้องมีอัตราว่างก่อน
 *                  ข้อมูลชุดนี้จับคู่ได้ 24 จาก 94 อัตรา ที่เหลือไม่มีคนสายงานและระดับ
 *                  เดียวกันในหน่วยที่กำลังคนเหลือกว่า
 *
 *   สลับตำแหน่ง    สองคนแลกที่กัน — ไม่ต้องใช้อัตราว่างเลย
 *                  กรอบอัตราของทั้งสองหน่วยไม่เปลี่ยน จำนวนคนไม่เปลี่ยน
 *                  จึงเป็นทางที่ยังเหลืออยู่ตอนไม่มีอัตราว่าง ซึ่งเป็นสถานการณ์ปกติ
 *
 * สองอย่างนี้ตอบคนละคำถามและมีข้อจำกัดคนละชุด ถ้ายุบเป็นรายการเดียว
 * ผู้ใช้จะแยกไม่ออกว่าแถวที่เห็นต้องรออัตราว่างหรือทำได้เลย
 *
 * ────────────────────────────────────────────────────────────────
 *  ⚠ ถ้อยคำในหน้านี้ห้ามแก้ให้ "ขายง่ายขึ้น"
 * ────────────────────────────────────────────────────────────────
 *
 * กฎเดียวกับ RotationModal และ Eligibility คือห้ามเขียนว่าระบบ "แนะนำ" ใคร
 * และห้ามแสดงลำดับเป็นคะแนนหรือเปอร์เซ็นต์ความเหมาะสม
 *
 * เหตุผลไม่ใช่การเลี่ยงคำ แต่เป็นข้อจำกัดของข้อมูล — สิ่งที่ระบบเห็นมีแค่สายงาน ระดับ
 * หน่วยงาน และวันครองตำแหน่ง ส่วนความสมัครใจ ภาระงานจริง ภูมิลำเนา และเหตุผลเชิงบริหาร
 * ซึ่งเป็นตัวตัดสินจริงในทางปฏิบัติ ไม่มีอยู่ในข้อมูลชุดนี้เลยสักอย่าง
 *
 * ในแท็บสลับตำแหน่ง ทั้งสองฝั่งของคู่ต้องแสดงเท่ากันเสมอ ห้ามทำให้ฝั่งไหนดูเป็น
 * "ผู้ถูกเลือก" และอีกฝั่งเป็น "ตัวเลือกสำรอง" เพราะการสลับกระทบทั้งสองคนเท่ากัน
 *
 * warnings ที่เซิร์ฟเวอร์ส่งมาต้องขึ้นบนหน้าจอเสมอ ห้ามยุบเก็บและห้ามตัดทิ้ง
 */
type Tab = 'fill' | 'swap';

/**
 * แปลงเลขสุทธิเป็นประโยค
 *
 * ⚠ ห้ามแสดงเป็นเลขเปล่า ๆ — "สุทธิ 0" อ่านแล้วเหมือนทำไปแล้วไม่ได้อะไร
 *   ทั้งที่ความหมายจริงคือ "ปิดได้เท่าที่เปิดใหม่ จำนวนอัตราว่างทั้งกระทรวงเท่าเดิม"
 *   ซึ่งเป็นข้อเท็จจริงที่ผู้บริหารต้องรู้ก่อนเซ็น ไม่ใช่คะแนน
 */
function netPhrase(net: number): string {
  if (net > 0) return `อัตราว่างในภาพรวมลดลง ${net} อัตรา`;
  if (net < 0) return `อัตราว่างในภาพรวมเพิ่มขึ้น ${-net} อัตรา`;
  return 'จำนวนอัตราว่างทั้งหมดเท่าเดิม';
}

export function RotationPlanModal({ onClose }: { onClose: () => void }) {
  const [tab, setTab] = useState<Tab>('fill');
  /** browse = ไล่ดูรายการ · draft = เปิดตะกร้าร่างที่หยิบไว้ */
  /* browse = เลือกอัตราและคน · board = กระดานเทียบของอัตราเดียว · draft = แผนภาพทั้งร่าง */
  const [view, setView] = useState<'browse' | 'board' | 'draft'>('browse');
  const draft = useDraft();
  /** id ของรายการที่กำลังเปิดกล่องเหตุผลอยู่ — null = ไม่ได้เปิด */
  const [reasonFor, setReasonFor] = useState<string | null>(null);
  const [confirming, setConfirming] = useState(false);
  const [pickedMatch, setPickedMatch] = useState<RotationMatch | null>(null);
  const [pickedSwap, setPickedSwap] = useState<RotationSwap | null>(null);
  const [q, setQ] = useState('');
  /* ⚠ ชิปตั้งต้นสองตัว — 687 ใบไม่มีจุดเริ่ม ถ้ามีแต่ช่องค้นหา ผู้ใช้ต้องรู้ก่อนว่าจะค้นอะไร
     ทั้งสองตัวเป็นการเรียง/กรองตรง ๆ จากข้อมูล ไม่ใช่ข้อเสนอว่าควรทำอัตราไหนก่อน */
  const [sortLongest, setSortLongest] = useState(false);
  const [hideDrafted, setHideDrafted] = useState(false);
  const closeBtn = useRef<HTMLButtonElement | null>(null);

  useEffect(() => {
    const returnTo = document.activeElement as HTMLElement | null;
    closeBtn.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onClose();
      }
    };
    document.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
      returnTo?.focus?.();
    };
  }, [onClose]);

  const plan = useQuery({ queryKey: ['rotation'], queryFn: api.rotation });
  const t = plan.data?.totals;
  const tenure = plan.data?.tenureMonths ?? 48;

  const needle = q.trim().toLowerCase();

  const matches = useMemo(() => {
    let list = plan.data?.matches ?? [];
    if (needle) {
      list = list.filter((m) =>
        [m.title, m.line, m.level, m.toUnit].join(' ').toLowerCase().includes(needle),
      );
    }
    if (hideDrafted) list = list.filter((m) => !targetInDraft(draft, m.posNo));
    /* ⚠ ต้องคัดลอกก่อนเรียง — plan.data.matches เป็นอาเรย์ของ react-query ที่แชร์กันทั้งแอป
       sort แก้ของเดิมในที่ ถ้าเรียงตรง ๆ ลำดับจากเซิร์ฟเวอร์จะเพี้ยนถาวรจนกว่าจะโหลดใหม่ */
    if (sortLongest) {
      list = [...list].sort((a, b) => (b.vacantMonths ?? -1) - (a.vacantMonths ?? -1));
    }
    return list;
  }, [plan.data, needle, hideDrafted, sortLongest, draft]);

  const swaps = useMemo(() => {
    const list = plan.data?.swaps ?? [];
    if (!needle) return list;
    return list.filter((s) =>
      [s.line, s.level, s.a.name, s.b.name, s.a.unit, s.b.unit].join(' ').toLowerCase().includes(needle),
    );
  }, [plan.data, needle]);

  /**
   * ตารางที่จะส่งออก — ตามแท็บที่เปิดอยู่
   *
   * ⚠ สองแท็บส่งออกคนละตาราง ไม่ใช่ตารางเดียวที่มีคอลัมน์ปนกัน
   * "เติมอัตราว่าง" คือ อัตราว่างหนึ่งอัตรา ต่อผู้ที่ย้ายมาได้หลายคน
   * "สลับตำแหน่ง" คือ คนสองคนแลกที่กัน
   * ยัดสองอย่างนี้ลงตารางเดียวจะได้คอลัมน์ที่ว่างครึ่งหนึ่งทุกแถว และอ่านไม่ออกทั้งใบ
   *
   * ⚠ แถวของ "เติมอัตราว่าง" กางเป็นหนึ่งแถวต่อผู้เข้าข่ายหนึ่งคน ไม่ใช่หนึ่งแถวต่ออัตรา
   * เพราะไฟล์ Excel มีไว้ให้กรองและเรียง ซึ่งทำกับเซลล์ที่ยัดหลายชื่อไว้ในช่องเดียวไม่ได้
   */
  const buildExport = useCallback((): ExportTable => {
    /* ⚠ ไฟล์ที่ส่งออกต้องบอกทุกตัวกรองที่ใช้อยู่ ห้ามตัดแถวออกเงียบ ๆ
       คนเปิดไฟล์ทีหลังไม่มีทางรู้ว่าที่เห็นไม่ครบเพราะติดตัวกรองอยู่ */
    const listNote = [
      needle ? `ค้นหา "${q.trim()}"` : null,
      hideDrafted ? 'เฉพาะอัตราที่ยังไม่อยู่ในร่าง' : null,
      sortLongest ? 'เรียงตามระยะเวลาที่ว่าง' : null,
    ]
      .filter(Boolean)
      .join(' · ');
    const filterNote = listNote ? ` · ${listNote}` : '';

    /* ⚠ ตอนเปิดตะกร้าอยู่ ปุ่มดาวน์โหลดต้องส่งออก "ร่าง" ไม่ใช่รายการทั้งหมดที่ระบบเจอ
       รายการทั้งหมดคือสิ่งที่ระบบคำนวณได้ ส่วนร่างคือสิ่งที่คนตัดสินใจแล้ว
       สองอย่างนี้เอาไปทำงานต่อคนละแบบ และอันหลังคือสิ่งที่เอาไปแนบเรื่องได้ */
    if (view === 'draft') return draftExport(draft, plan.data?.warnings ?? []);
    const warnings = [
      ...(plan.data?.warnings ?? []),
      'รายชื่อนี้คือผู้ที่อยู่สายงานและระดับเดียวกันในหน่วยอื่น ไม่ใช่ผลการประเมินความเหมาะสม ' +
        'และไม่ได้ตรวจมาตรฐานกำหนดตำแหน่ง เพราะเป็นการย้ายในระดับเดิม ไม่ใช่การเลื่อนระดับ',
      'กฎ ก.พ. ว่าด้วยการย้าย การโอน หรือการเลื่อนข้าราชการพลเรือนสามัญ พ.ศ. 2564 ข้อ 3 ' +
        'กำหนดให้พิจารณาโดยคำนึงถึงศักยภาพ ผลสัมฤทธิ์ของงาน สมรรถนะ ประวัติการรับราชการ ' +
        'ความรู้ความสามารถ ตลอดจนพฤติกรรมทางจริยธรรมและความประพฤติ ' +
        'ซึ่งทั้งหมดอยู่นอกข้อมูลที่ระบบนี้มี',
    ];

    if (tab === 'swap') {
      return {
        title: 'การสับเปลี่ยนตำแหน่ง',
        subtitle: `ครองตำแหน่งเดิมตั้งแต่ ${tenure} เดือนขึ้นไปถือว่าอยู่นาน · ${swaps.length} คู่${filterNote}`,
        warnings,
        columns: [
          { key: 'line', label: 'สายงาน', width: 28 },
          { key: 'level', label: 'ระดับ', width: 18 },
          { key: 'aName', label: 'คนที่ 1', width: 24 },
          { key: 'aUnit', label: 'หน่วยงานของคนที่ 1', width: 32 },
          { key: 'aMonths', label: 'ครองมาแล้ว (เดือน)', width: 16, numeric: true },
          { key: 'bName', label: 'คนที่ 2', width: 24 },
          { key: 'bUnit', label: 'หน่วยงานของคนที่ 2', width: 32 },
          { key: 'bMonths', label: 'ครองมาแล้ว (เดือน)', width: 16, numeric: true },
        ],
        rows: swaps.map((sw) => ({
          line: sw.line,
          level: levelLabel(sw.level),
          aName: sw.a.name,
          aUnit: sw.a.unit,
          aMonths: sw.a.monthsInPosition,
          bName: sw.b.name,
          bUnit: sw.b.unit,
          bMonths: sw.b.monthsInPosition,
        })),
      };
    }

    return {
      title: 'การย้ายเพื่อบรรจุตำแหน่งว่าง',
      subtitle: `ตำแหน่งว่างที่มีผู้ย้ายมาดำรงตำแหน่งได้ ${matches.length} อัตรา${filterNote}`,
      warnings,
      columns: [
        { key: 'posNo', label: 'เลขที่ตำแหน่งว่าง', width: 15 },
        { key: 'title', label: 'ตำแหน่งว่าง', width: 30 },
        { key: 'line', label: 'สายงาน', width: 26 },
        { key: 'level', label: 'ระดับ', width: 18 },
        { key: 'toUnit', label: 'หน่วยงานปลายทาง', width: 32 },
        { key: 'vacantMonths', label: 'ว่างมาแล้ว (เดือน)', width: 16, numeric: true },
        { key: 'cName', label: 'ผู้ที่ย้ายมาได้', width: 24 },
        { key: 'cUnit', label: 'หน่วยงานต้นทาง', width: 32 },
        { key: 'cMonths', label: 'ครองตำแหน่งเดิม (เดือน)', width: 18, numeric: true },
      ],
      rows: matches.flatMap((m) =>
        m.candidates.map((c) => ({
          posNo: m.posNo,
          title: m.title,
          line: m.line,
          level: levelLabel(m.level),
          toUnit: m.toUnit,
          vacantMonths: m.vacantMonths,
          cName: c.name,
          cUnit: c.fromUnit,
          cMonths: c.monthsInPosition,
        })),
      ),
    };
  }, [tab, matches, swaps, plan.data, needle, q, tenure, view, draft, hideDrafted, sortLongest]);

  return (
    <div
      className="pmodal-back"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="pmodal is-wide" role="dialog" aria-modal="true" aria-label="แผนหมุนเวียนกำลังคน">
        <header className="pmodal-hd">
          <span className="simicon" aria-hidden="true"><RetweetOutlined /></span>
          <div>
            <span className="kind">การย้ายและสับเปลี่ยนตำแหน่ง</span>
            <b>
              {t
                ? `บรรจุตำแหน่งว่างได้ ${t.matched} อัตรา · สับเปลี่ยนตำแหน่งได้ ${t.swapsShown} คู่`
                : 'กำลังคำนวณ…'}
            </b>
            <span className="sub">
              การย้ายในระดับเดิมข้ามส่วนราชการ ไม่ใช่การเลื่อนระดับ จึงไม่ได้ตรวจมาตรฐานกำหนดตำแหน่ง
            </span>
          </div>
          {plan.data && (
            <ExportButtons
              build={buildExport}
              /* ⚠ เฉพาะตอนอยู่ในร่างเท่านั้น — หน้ารายการยังออกเป็นตารางเหมือนเดิม
                 เพราะรายการ 687 อัตราไม่ใช่หนังสือเสนอ แต่เป็นข้อมูลให้ไล่ดู */
              pdfOverride={
                view === 'draft'
                  ? () => exportRotationMemo(draft, plan.data?.warnings ?? [])
                  : undefined
              }
            />
          )}
          <button type="button" onClick={onClose} aria-label="ปิดหน้าต่าง" ref={closeBtn}>✕</button>
        </header>

        {view === 'board' && pickedMatch ? (
          <CandidateBoard
            match={pickedMatch}
            matches={plan.data?.matches ?? []}
            draft={draft}
            onBack={() => setView('browse')}
          />
        ) : view === 'draft' ? (
          <DraftPanel
            items={draft}
            tenureMonths={tenure}
            onEditReason={(id) => setReasonFor(id)}
            onBack={() => setView('browse')}
          />
        ) : (
        <div className={tab === 'fill' ? 'simbody has-rail' : 'simbody'}>
          <aside className="simlist">
            <div className="rottabs" role="tablist" aria-label="เลือกวิธีการย้ายหรือสับเปลี่ยน">
              <button
                type="button"
                role="tab"
                aria-selected={tab === 'fill'}
                onClick={() => setTab('fill')}
              >
                บรรจุตำแหน่งว่าง
                {t && <em>{t.matched}</em>}
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={tab === 'swap'}
                onClick={() => setTab('swap')}
              >
                สับเปลี่ยนตำแหน่ง
                {t && <em>{t.swapsShown}</em>}
              </button>
            </div>

            <div className="simsearch">
              <input
                type="search"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder={
                  tab === 'fill' ? 'ค้นหาตำแหน่ง สายงาน หรือหน่วยงาน' : 'ค้นหาชื่อ สายงาน หรือหน่วยงาน'
                }
                aria-label="ค้นหาในรายการ"
              />
            </div>

            {/* ⚠ คลาสต้องเป็น .simchip ไม่ใช่ปุ่มเปล่า ๆ
                กฎ .simlist button ที่อยู่ก่อนหน้าตั้ง display:block กับ width:100% ไว้
                ปุ่มที่ไม่มีคลาสของตัวเองจะกลายเป็นแถบเต็มความกว้างทันที
                กับดักเดียวกับ .side, .warn และ .pmodal-hd button ที่เคยชนกันมาแล้ว */}
            {tab === 'fill' && (
              <div className="simchips">
                <button
                  type="button"
                  className="simchip"
                  aria-pressed={sortLongest}
                  onClick={() => setSortLongest((v) => !v)}
                >
                  ว่างนานที่สุดก่อน
                </button>
                <button
                  type="button"
                  className="simchip"
                  aria-pressed={hideDrafted}
                  onClick={() => setHideDrafted((v) => !v)}
                >
                  ยังไม่อยู่ในร่าง
                </button>
              </div>
            )}

            {plan.isPending && <p className="state sm">กำลังคำนวณแผนหมุนเวียน…</p>}
            {plan.isError && <p className="state error sm">{String(plan.error)}</p>}

            {tab === 'fill' && plan.data && matches.length === 0 && (
              <p className="state sm">ไม่พบอัตราว่างที่ตรงกับคำค้น</p>
            )}
            {tab === 'swap' && plan.data && swaps.length === 0 && (
              <p className="state sm">ไม่พบคู่สลับที่ตรงกับคำค้น</p>
            )}

            {tab === 'fill' ? (
              <ul>
                {matches.map((m, i) => (
                  <li key={`${m.posNo ?? 'no'}-${m.toUnit}-${i}`}>
                    <button
                      type="button"
                      aria-pressed={pickedMatch === m}
                      onClick={() => setPickedMatch(m)}
                    >
                      {/* ⚠ ชื่อหน่วยงานต้องอยู่บรรทัดแรก ไม่ใช่ชื่อตำแหน่ง
                          ในรายการนี้ชื่อตำแหน่งซ้ำกันเกือบทุกใบ ("นักบริหาร" ห้าใบติดกัน)
                          สิ่งที่ทำให้ใบหนึ่งต่างจากอีกใบคือหน่วยงาน ตัวที่ต่างต้องเด่นที่สุด
                          ⚠ เอา "หน่วยปลายทางมีคนครอง N%" ออกจากรายการแล้ว —
                            มันเป็นเหตุผลของการเรียงลำดับ ไม่ใช่สิ่งที่ช่วยให้เลือกใบ
                            และมันเกือบเท่ากันทุกใบ จึงเป็นสีแดงที่ไม่ได้บอกอะไรเลย */}
                      <b>{leaf(m.toUnit)}</b>
                      <span className="un">
                        {m.title} · {levelLabel(m.level)}
                      </span>
                      <span className="du">
                        {m.vacantMonths !== null
                          ? `ว่างมาแล้ว ${duration(m.vacantMonths)}`
                          : 'ไม่ทราบว่าว่างมานานแค่ไหน'}
                      </span>
                      <span className="lv">
                        {/* ⚠ ต้องเห็นตั้งแต่ในรายการว่าอัตรานี้มีคนถูกเสนอแล้ว
                            ไม่งั้นผู้ใช้จะกดเข้าไปแล้วเจอปุ่มที่กดไม่ได้ทุกใบโดยไม่รู้ว่าทำไม */}
                        {targetInDraft(draft, m.posNo)
                          ? `เสนอ ${targetInDraft(draft, m.posNo)?.person.name} ไว้แล้ว`
                          : `ย้ายมาลงได้ ${m.candidates.length} คน`}
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            ) : (
              <ul>
                {swaps.map((s, i) => (
                  <li key={`${s.a.personaId ?? s.a.name}-${s.b.personaId ?? s.b.name}-${i}`}>
                    <button
                      type="button"
                      aria-pressed={pickedSwap === s}
                      onClick={() => setPickedSwap(s)}
                    >
                      <b>
                        {leaf(s.a.unit)} <SwapRightOutlined aria-label="สลับกับ" /> {leaf(s.b.unit)}
                      </b>
                      <span className="lv">
                        {s.line} · {levelLabel(s.level)}
                      </span>
                      <span className="un">
                        {s.a.name} · {s.b.name}
                      </span>
                      <span className="du">
                        {s.bothLongTenure
                          ? 'ครองตำแหน่งเดิมนานทั้งคู่'
                          : 'ครองตำแหน่งเดิมนานฝั่งเดียว'}
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </aside>

          <div className="simmain">
            {tab === 'fill' &&
              (pickedMatch ? (
                <MatchDetail
                  match={pickedMatch}
                  tenureMonths={tenure}
                  draft={draft}
                  onAsk={(id) => setReasonFor(id)}
                  onOpenBoard={() => setView('board')}
                />
              ) : (
                <div className="simempty">
                  <RetweetOutlined aria-hidden="true" />
                  <b>เลือกอัตราว่างจากรายการ</b>
                  <p>
                    ระบบจะแสดงผู้ที่อยู่สายงานและระดับเดียวกันในหน่วยอื่นซึ่งมีกำลังคนเหลือมากกว่า
                    เรียงตามระยะเวลาครองตำแหน่งปัจจุบัน — ไม่ใช่ลำดับความเหมาะสม
                  </p>
                  {t && (
                    <p>
                      อีก {t.unmatched} อัตราไม่มีคู่หมุนเวียนในกรม
                      ต้องสรรหาจากภายนอกหรือเลื่อนระดับแทน — หรือดูทางสลับตำแหน่งซึ่งไม่ต้องใช้อัตราว่าง
                    </p>
                  )}
                </div>
              ))}

            {tab === 'swap' &&
              (pickedSwap ? (
                <SwapDetail swap={pickedSwap} tenureMonths={tenure} />
              ) : (
                <div className="simempty">
                  <SwapRightOutlined aria-hidden="true" />
                  <b>เลือกคู่สลับจากรายการ</b>
                  <p>
                    สองคนสายงานและระดับเดียวกันคนละหน่วยงาน แลกที่กันได้โดยไม่ต้องใช้อัตราว่าง
                    กรอบอัตราและจำนวนคนของทั้งสองหน่วยไม่เปลี่ยน
                  </p>
                  {t && t.swapsFound > t.swapsShown && (
                    <p>
                      จับคู่ได้ทั้งหมด {t.swapsFound.toLocaleString('th-TH')} คู่
                      แสดง {t.swapsShown} คู่ โดยจำกัดไม่ให้คนคนเดียวโผล่ซ้ำเกิน 3 คู่
                    </p>
                  )}
                </div>
              ))}

            {plan.data && <PlanLimits warnings={plan.data.warnings} />}
          </div>

          {tab === 'fill' && (
            <DraftRail
              items={draft}
              onEditReason={(id) => setReasonFor(id)}
              onOpenMap={() => setView('draft')}
              onSubmit={() => setConfirming(true)}
            />
          )}
        </div>
        )}

        {/* ⚠ แถบนี้ต้องอยู่ในสายตาตลอดเวลา ไม่ใช่ไอคอนตะกร้าที่ต้องกดเปิดไปดู
            ตัวเลข "ปิด/เปิดใหม่/สุทธิ" เป็นสิ่งที่ผู้ใช้ต้องเห็นระหว่างกำลังหยิบ
            เพราะการย้ายหนึ่งครั้งเปิดอัตราว่างใหม่เสมอ ซึ่งเป็นเรื่องที่คนลืมบ่อยที่สุด */}
        <DraftBar
          items={draft}
          view={view}
          onOpen={() => setView(view === 'draft' ? 'browse' : 'draft')}
          onSubmit={() => setConfirming(true)}
        />
      </div>

      {reasonFor && (
        <ReasonModal
          item={draft.find((it) => it.id === reasonFor) ?? null}
          onClose={() => setReasonFor(null)}
        />
      )}
      {confirming && (
        <SubmitModal
          items={draft}
          tenureMonths={tenure}
          build={() => draftExport(draft, plan.data?.warnings ?? [])}
          pdfOverride={() => exportRotationMemo(draft, plan.data?.warnings ?? [])}
          onFix={(id) => {
            setConfirming(false);
            setView('draft');
            if (id) setReasonFor(id);
          }}
          onClose={() => setConfirming(false)}
        />
      )}
    </div>
  );
}

/* ---------------- แท็บ 1: ผู้ที่ย้ายเข้าอัตราว่างได้ ---------------- */

function MatchDetail({
  match,
  tenureMonths,
  draft,
  onAsk,
  onOpenBoard,
}: {
  match: RotationMatch;
  tenureMonths: number;
  draft: DraftItem[];
  /** ไปหน้ากระดานเทียบของอัตรานี้ */
  onOpenBoard: () => void;
  /** เปิดกล่องเหตุผลของรายการที่อยู่ในร่างแล้ว — เรียกจากปุ่มเท่านั้น ไม่ใช่ตอนหยิบ */
  onAsk: (id: string) => void;
}) {
  /* ⚠ ห้ามเปิดกล่องเหตุผลตรงนี้
     ตอนหยิบผู้ใช้กำลังไล่ดูผู้อยู่ในข่าย โมดัลที่เด้งขึ้นมาทับคือการขัดจังหวะ
     รายการไปโผล่ในแถบร่างด้านขวาทันทีอยู่แล้ว พร้อมปุ่ม "ระบุเหตุผล" ที่กดเมื่อพร้อม */
  const pick = (c: RotationCandidate) => {
    addToDraft(
      {
        personaId: c.personaId,
        name: c.name,
        posNo: c.posNo,
        title: c.title,
        unit: c.fromUnit,
        level: match.level,
        monthsInPosition: c.monthsInPosition,
        retireInYears: c.retireInYears,
      },
      {
        posNo: match.posNo,
        title: match.title,
        unit: match.toUnit,
        level: match.level,
        line: match.line,
        vacantMonths: match.vacantMonths,
      },
    );
  };

  /* อัตรานี้มีคนถูกเสนอไว้แล้วหรือยัง — เก้าอี้ตัวเดียวรับได้คนเดียว ดู targetInDraft
     ⚠ เป็นเรื่องของ "อัตรา" ไม่ใช่ของ "คน" จึงคำนวณครั้งเดียวตรงนี้
       และแสดงผลครั้งเดียวบนหัวรายการ ไม่ใช่พิมพ์ซ้ำในการ์ดของทุกคน */
  const taken = targetInDraft(draft, match.posNo);
  const shortlist = shortlistOf(draft, match.posNo);

  return (
    <>
      <section className="pmodal-sec">
        <h4>อัตราว่างที่กำลังดู</h4>
        <div className="simtarget">
          <b>{match.title}</b>
          <span>
            {levelLabel(match.level)}
            {match.posNo && ` · เลขที่ ${match.posNo}`}
            {match.line && ` · สายงาน${match.line}`}
          </span>
          <span className="un">{match.toUnit}</span>
          {/* ⚠ 0% อ่านแล้วเหมือนระบบพัง ต้องเขียนเป็นประโยค
              และหน่วยที่ยังไม่มีใครครองเลยคือข้อเท็จจริงที่ต่างจาก "ครองไปแล้วบางส่วน" จริง ๆ */}
          <span className="du">
            {match.toUnitRatio <= 0
              ? 'หน่วยงานนี้ยังไม่มีผู้ครองตำแหน่งในกรอบอัตราเลย'
              : `หน่วยนี้มีคนครอง ${pct(match.toUnitRatio)} ของกรอบอัตรา`}
            {match.vacantMonths !== null && ` · อัตรานี้ว่างมาแล้ว ${duration(match.vacantMonths)}`}
          </span>
        </div>
      </section>

      <section className="pmodal-sec">
        {/* ⚠ หัวข้อนี้ต้องอ่านว่า "ใครอยู่ในข่าย" ไม่ใช่ "ใครควรย้าย" — ดูเหตุผลที่หัวไฟล์ */}
        <h4>
          ผู้ที่ย้ายมาลงอัตรานี้ได้ <span className="vc-count">{match.candidates.length} คน</span>
        </h4>

        {/* ⚠ พูดครั้งเดียวตรงนี้ ไม่ใช่พิมพ์ซ้ำในทุกการ์ด
            เดิมประโยคเดียวกันโผล่ในการ์ดที่เหลือทุกใบ (แปดคนก็เจ็ดครั้ง) กลายเป็นกำแพงคำซ้ำ
            ที่กลบข้อมูลของแต่ละคนซึ่งเป็นสิ่งที่ผู้ใช้เข้ามาอ่านจริง ๆ

            ⚠ ตอนนี้เป็นข้อมูล ไม่ใช่ข้อห้าม — ใส่ใครเข้าบัญชีพิจารณาก็ได้ไม่จำกัด
              ข้อห้ามมีข้อเดียวคือติดธง "เสนอ" ได้คนเดียว ซึ่งบังคับตอนกดติดธง */}
        {taken && (
          <p className="rotheld">
            <span>
              อัตรานี้ติดธงเสนอ <b>{taken.person.name}</b> ไว้แล้ว — ยังใส่ท่านอื่นเข้าบัญชีพิจารณาได้
            </span>
          </p>
        )}

        {shortlist.length > 0 && (
          <p className="rotheld is-board">
            <span>
              บัญชีผู้ได้รับการพิจารณาของอัตรานี้มี <b>{shortlist.length} ท่าน</b>
            </span>
            <button type="button" className="cb-open" onClick={onOpenBoard}>
              เทียบตามเกณฑ์
            </button>
          </p>
        )}
        <p className="pmodal-note">
          เรียงตามสองอย่างที่วัดได้ — หน่วยเดิมมีคนครบแค่ไหน และอยู่ตำแหน่งเดิมมานานแค่ไหน
          (เกิน {duration(tenureMonths)} ถือว่าถึงกำหนดหมุนเวียน) <b>ไม่ใช่ลำดับความเหมาะสม</b>
        </p>
        <ol className="rotcand">
          {match.candidates.map((c, i) => {
            const picked = inDraft(draft, c.personaId, c.name, match.posNo);
            /* ⚠ เป็นข้อมูลประกอบ ไม่ใช่ข้อห้าม — คนหนึ่งคนอยู่ในบัญชีพิจารณาได้หลายอัตรา
               ที่ห้ามคือถูก "เสนอ" มากกว่าหนึ่งอัตรา ซึ่งบังคับตอนกดติดธงที่กระดานเทียบ */
            const proposedElsewhere = personProposed(draft, c.personaId, c.name);
            return (
            <li key={`${c.personaId ?? c.name}-${i}`} className={picked ? 'is-picked' : undefined}>
              <div className="rc-hd">
                <b>{c.name}</b>
                {c.longTenure && <span className="rc-tag is-due">ครองตำแหน่งเดิมนาน</span>}
                {c.retireInYears !== null && c.retireInYears <= 1 && (
                  <span className="rc-tag is-warn">เกษียณภายใน 1 ปี</span>
                )}
              </div>
              {/* ⚠ เดิมเป็นสามบรรทัดสีเทาน้ำหนักเท่ากัน อ่านแล้วไม่รู้ว่าบรรทัดไหนสำคัญ
                  และมีเปอร์เซ็นต์สองตัว (ต้นทาง/ปลายทาง) ให้ผู้อ่านจำไปเทียบกันเอง
                  ตอนนี้แปลงเป็นประโยคเดียวว่าย้ายออกแล้วหน่วยเดิมเป็นอย่างไร */}
              <span className="rc-sub">
                ตอนนี้อยู่ที่ <b>{leaf(c.fromUnit)}</b> — {c.title}
                {c.posNo && ` (เลขที่ ${c.posNo})`}
              </span>
              <span className="rc-sub">
                {c.monthsInPosition === null
                  ? 'ไม่มีวันครองตำแหน่งในข้อมูล'
                  : `อยู่ตำแหน่งเดิมมา ${duration(c.monthsInPosition)}`}
                {' · '}
                {c.fromUnitRatio >= match.toUnitRatio
                  ? 'หน่วยเดิมมีคนครบกว่าหน่วยปลายทาง ย้ายออกแล้วยังเหลือคน'
                  : 'หน่วยเดิมมีคนน้อยกว่าหน่วยปลายทาง'}
              </span>

              {picked?.reason && picked.reason.clauses.length > 0 && (
                <p className="rd-why">
                  <span className="rd-cl">ข้อ 3 ({picked.reason.clauses.join(', ')})</span>
                  {picked.reason.text || picked.reason.clauses.map(clauseLabel).join(' · ')}
                </p>
              )}

              {/* ⚠ ไม่มีสถานะ "หยิบไม่ได้" อีกแล้ว — ใส่บัญชีพิจารณาได้เสมอ
                  ข้อห้ามทั้งหมดย้ายไปอยู่ที่การติดธงเสนอบนกระดานเทียบ ซึ่งเป็นที่เดียว
                  ที่ตัวเลขบนหน้าจอขึ้นกับมัน */}
              <div className="rd-act">
                {picked ? (
                  <>
                    <span className="rd-in">
                      <CheckOutlined aria-hidden="true" />{' '}
                      {picked.status === 'proposed' ? 'ติดธงเสนอแล้ว' : 'อยู่ในบัญชีพิจารณา'}
                    </span>
                    <button type="button" className="rd-link" onClick={() => onAsk(picked.id)}>
                      {picked.reason ? 'แก้เหตุผล' : 'ระบุเหตุผล'}
                    </button>
                    <button type="button" className="rd-link" onClick={() => removeFromDraft(picked.id)}>
                      เอาออกจากบัญชี
                    </button>
                  </>
                ) : (
                  <>
                    <button type="button" className="rd-pick" onClick={() => pick(c)}>
                      <PlusOutlined aria-hidden="true" /> ใส่บัญชีพิจารณา
                    </button>
                    {proposedElsewhere && (
                      <span className="rd-else">
                        ท่านนี้ติดธงเสนอไว้แล้วที่{proposedElsewhere.target.title}{' '}
                        {leafUnit(proposedElsewhere.target.unit)}
                      </span>
                    )}
                  </>
                )}
              </div>
            </li>
            );
          })}
        </ol>
      </section>
    </>
  );
}

/**
 * ข้อจำกัดที่ต้องอ่านก่อนใช้ — แยกเป็นสองกลุ่ม
 *
 * ────────────────────────────────────────────────────────────────
 *  ⚠ ห้ามย่อ ห้ามตัดข้อความ
 * ────────────────────────────────────────────────────────────────
 *
 * ตัวบทมาจาก server/src/domain/rules/transferRules.ts ซึ่งเขียนกำกับไว้ว่า
 * ห้ามย่อเพื่อความสวยงามของหน้าจอ ที่ทำตรงนี้จึงเป็นการ "จัดกลุ่มและพับเก็บ"
 * ไม่ใช่การตัดคำ — ทุกตัวอักษรยังอยู่ครบ ห่างออกไปหนึ่งคลิก
 *
 * เดิมทั้งสิบเอ็ดข้อเป็นกล่องสีทองหน้าตาเหมือนกันเรียงต่อกันยาวเกินหนึ่งจอ
 * ผลคือหกข้อแรกซึ่งเปลี่ยนวิธีอ่านหน้าจอจริง ๆ จมหายไปกับตัวบทกฎหมายที่ยาวกว่า
 * และผู้ใช้เลื่อนผ่านทั้งก้อนโดยไม่อ่านสักข้อ
 *
 * ⚠ ตัวแยกกลุ่มดูจากคำขึ้นต้นของประโยค ซึ่งผูกกับถ้อยคำใน transferRules.ts
 *   ถ้าวันหนึ่งถ้อยคำเปลี่ยนจนจับไม่ได้ ข้อนั้นจะตกมาอยู่กลุ่มบนซึ่งแสดงเต็มเสมอ
 *   พังไปทางที่ปลอดภัย คือไม่มีอะไรถูกซ่อนโดยไม่ตั้งใจ
 */
function isLegalCitation(w: string): boolean {
  return /^(กฎ ก\.พ\.|มาตรา )/.test(w);
}

function PlanLimits({ warnings }: { warnings: string[] }) {
  const limits = warnings.filter((w) => !isLegalCitation(w));
  const laws = warnings.filter(isLegalCitation);

  return (
    <section className="pmodal-sec">
      <h4>
        ข้อจำกัดที่ต้องอ่านก่อนใช้ <span className="vc-count">{limits.length} ข้อ</span>
      </h4>
      <ul className="rotwarn">
        {limits.map((w) => (
          <li key={w}>{w}</li>
        ))}
      </ul>

      {laws.length > 0 && (
        <details className="rotlaw">
          <summary>
            ตัวบทที่หน้านี้อ้างอิง <em>{laws.length} ข้อ · กดเพื่ออ่านฉบับเต็ม</em>
          </summary>
          <ul>
            {laws.map((w) => (
              <li key={w}>{w}</li>
            ))}
          </ul>
        </details>
      )}
    </section>
  );
}

/* ---------------- แท็บ 2: คู่สลับตำแหน่ง ---------------- */

function SwapDetail({ swap, tenureMonths }: { swap: RotationSwap; tenureMonths: number }) {
  return (
    <>
      <section className="pmodal-sec">
        <h4>คู่ที่กำลังดู</h4>
        <div className="simtarget">
          <b>
            {swap.line} · {levelLabel(swap.level)}
          </b>
          <span>สายงานและระดับตรงกันทั้งสองฝั่ง จึงเป็นการสลับ ไม่ใช่การเลื่อนหรือลดระดับ</span>
          <span className="un">
            {swap.bothLongTenure
              ? `ครองตำแหน่งเดิมเกิน ${duration(tenureMonths)} ทั้งสองฝั่ง`
              : `ครองตำแหน่งเดิมเกิน ${duration(tenureMonths)} ฝั่งเดียว`}
          </span>
        </div>
      </section>

      <section className="pmodal-sec">
        <h4>ทั้งสองฝั่งของคู่</h4>
        {/* ⚠ ทั้งสองฝั่งต้องแสดงเหมือนกันทุกอย่าง ห้ามทำฝั่งไหนให้เด่นกว่า
            การสลับกระทบทั้งสองคนเท่ากัน ไม่มีใครเป็นผู้ถูกเลือกและอีกคนเป็นตัวสำรอง */}
        <div className="swappair">
          <SwapCard side={swap.a} />
          <div className="swaparrow" aria-hidden="true">
            <SwapRightOutlined />
          </div>
          <SwapCard side={swap.b} />
        </div>
        <p className="pmodal-note">
          สลับแล้วกรอบอัตราและจำนวนคนของทั้งสองหน่วยไม่เปลี่ยน เปลี่ยนแค่ว่าใครนั่งที่ไหน
          จึงทำได้โดยไม่ต้องรออัตราว่าง
        </p>
      </section>
    </>
  );
}

function SwapCard({ side }: { side: SwapSide }) {
  return (
    <div className="swapcard">
      <div className="rc-hd">
        <b>{side.name}</b>
        {side.longTenure && <span className="rc-tag is-due">ครองตำแหน่งเดิมนาน</span>}
        {side.retireInYears !== null && side.retireInYears <= 1 && (
          <span className="rc-tag is-warn">เกษียณภายใน 1 ปี</span>
        )}
      </div>
      <span className="rc-sub">
        {side.title}
        {side.posNo && ` · เลขที่ ${side.posNo}`}
      </span>
      <span className="rc-sub">{side.unit}</span>
      <span className="rc-sub">
        {side.monthsInPosition === null
          ? 'ไม่มีวันครองตำแหน่งในข้อมูล'
          : `ครองตำแหน่งปัจจุบันมาแล้ว ${duration(side.monthsInPosition)}`}
      </span>
    </div>
  );
}

/** เอาเฉพาะชั้นท้ายสุดของเส้นทางหน่วยงาน — รายการฝั่งซ้ายแคบเกินกว่าจะใส่ทั้งเส้น */
function leaf(unit: string): string {
  const parts = unit.split(' > ');
  return parts[parts.length - 1] ?? unit;
}

function pct(ratio: number): string {
  return `${Math.round(ratio * 100)}%`;
}

function duration(months: number): string {
  const y = Math.floor(months / 12);
  const m = months % 12;
  if (!y) return `${m} เดือน`;
  return m ? `${y} ปี ${m} เดือน` : `${y} ปี`;
}

/* ================================================================
   ร่างรอบโยกย้าย
   ================================================================

   หน้าจอนี้เปลี่ยนหน้าเดิมจาก "รายการให้อ่าน" เป็น "ที่ร่างคำสั่ง"
   ⚠ หลักเดียวที่ทุกส่วนอยู่ใต้มัน — ระบบเตรียมเรื่องและบันทึกเหตุผล คนตัดสิน
     ห้ามมีที่ไหนในนี้ให้คะแนน จัดอันดับ หรือบอกว่าใครเหมาะกว่ากัน
     ขั้นจัดลำดับความเหมาะสมเป็นของคณะกรรมการตามกฎหมาย ไม่ใช่ของระบบนี้
   ================================================================ */

/** แถบสรุปร่างที่ก้นหน้าต่าง — เห็นตลอดเวลาไม่ว่าอยู่แท็บไหน */
function DraftBar({
  items,
  view,
  onOpen,
  onSubmit,
}: {
  items: DraftItem[];
  view: 'browse' | 'board' | 'draft';
  onOpen: () => void;
  onSubmit: () => void;
}) {
  /* ⚠ นับเฉพาะผู้ที่ติดธง "เสนอ" ให้ตรงกับตัวเลข ปิด/เปิดใหม่ ข้างล่าง
     ถ้านับทั้งบัญชี จะได้หัวข้อว่า "4 รายการ" คู่กับ "ปิดอัตราว่างเดิม 1"
     บนแถบเดียวกัน ซึ่งอ่านแล้วเหมือนระบบนับผิด */
  const proposed = items.filter((it) => it.status === 'proposed');
  const considered = items.length - proposed.length;
  const s = summarise(items);
  const noReason = proposed.filter((it) => !it.reason || it.reason.clauses.length === 0).length;

  if (proposed.length === 0) {
    return (
      <div className="rdbar is-empty">
        <span>
          {considered > 0 ? (
            <>
              มีผู้ได้รับการพิจารณา <b>{considered} ท่าน</b> แต่ยังไม่มีใครติดธง &ldquo;เสนอ&rdquo; —
              ยอดจึงยังเป็นศูนย์ ติดธงได้ที่หน้าเทียบตามเกณฑ์
            </>
          ) : (
            <>
              ยังไม่มีรายการในร่าง — กด <b>ใส่บัญชีพิจารณา</b> ที่ผู้อยู่ในข่าย
              แล้วเทียบตามเกณฑ์ก่อนติดธงเสนอ
            </>
          )}
        </span>
      </div>
    );
  }

  return (
    <div className="rdbar">
      <div className="rd-sum">
        <b>ร่างรอบโยกย้าย</b>
        <span>
          เสนอ {proposed.length} รายการ
          {considered > 0 && <em> · พิจารณาอยู่อีก {considered} ท่าน</em>}
          {noReason > 0 && <em> · {noReason} รายการยังไม่ระบุเหตุผล</em>}
        </span>
      </div>

      {/* ⚠ ต้องเขียนเป็นประโยค ไม่ใช่ตัวเลขสามตัวเรียงกัน
          "ปิด 4 · เปิดใหม่ 4 · สุทธิ 0" อ่านแล้วไม่รู้ว่า 0 แปลว่าอะไร
          และห้ามตัดเหลือแค่ "ปิดช่องว่าง" เพราะการย้ายหนึ่งครั้งเปิดอัตราว่างใหม่เสมอ
          ตัวเลขที่บอกครึ่งเดียวคือตัวเลขที่หลอก */}
      <div className="rd-net">
        <span className="rd-line">
          ปิดอัตราว่างเดิม <b>{s.closed}</b> · เปิดอัตราว่างใหม่ <b className="is-alert">{s.opened}</b>
          {' — '}
          <b>{netPhrase(s.net)}</b>
        </span>
        <span className="rd-src">คำนวณจากข้อมูล ไม่ใช่ AI</span>
      </div>

      <button type="button" className="rd-ghost" onClick={onOpen}>
        {view === 'draft' ? 'กลับไปเลือกต่อ' : 'เปิดร่าง'}
      </button>
      <button type="button" className="rd-primary" onClick={onSubmit}>
        ออกร่างเสนอ <ArrowRightOutlined aria-hidden="true" />
      </button>
    </div>
  );
}

/**
 * แถบร่างด้านขวา — เห็นตลอดเวลาที่กำลังเลือก
 *
 * ────────────────────────────────────────────────────────────────
 *  ⚠ ทำไมร่างต้องอยู่ในจอเดียวกับรายการ ไม่ใช่หน้าที่ต้องกดสลับไป
 * ────────────────────────────────────────────────────────────────
 *
 * ของเดิมกด "เปิดร่าง" แล้วทั้งหน้าเปลี่ยนเป็นร่าง ต้องกด "กลับไปเลือกต่อ" เพื่อกลับมา
 * ผลคือระหว่างไล่เลือกคน ผู้ใช้มองไม่เห็นว่าตัวเองหยิบอะไรไปแล้วบ้าง
 * และพอสลับกลับมาก็ลืมว่าค้างอยู่ที่อัตราไหน
 *
 * แถบนี้กับ .rdbar ด้านล่างเป็นของสิ่งเดียวกัน จึงห้ามโผล่พร้อมกัน —
 * ตัวสลับอยู่ใน CSS (.simbody.has-rail + .rdbar { display: none })
 * ที่จอแคบกว่า 1120px แถบนี้หายไปและ .rdbar กลับมาแทน
 *
 * ⚠ แถบนี้แสดงแค่ชื่อกับปลายทาง เพราะกว้าง 300px
 * ภาพเต็มของร่าง (แผนภาพ ลูกโซ่ ข้อควรแก้ ผู้ช่วยอ่านเหตุผล) ยังอยู่ที่ DraftPanel
 * ปุ่ม "ดูเป็นแผนภาพเต็มจอ" คือทางไปที่นั่น
 */
function DraftRail({
  items,
  onEditReason,
  onOpenMap,
  onSubmit,
}: {
  items: DraftItem[];
  onEditReason: (id: string) => void;
  onOpenMap: () => void;
  onSubmit: () => void;
}) {
  /* ⚠ แถบนี้แสดงเฉพาะผู้ที่ติดธง "เสนอ" — คนที่ยังอยู่ในบัญชีพิจารณาไม่ขึ้นเป็นแถว
     เพราะแถวในแถบนี้อ่านว่า "การย้ายหนึ่งรายการ" และตัวเลขข้างล่างก็นับแบบนั้น
     ถ้าเอาบัญชีพิจารณามาขึ้นด้วย จะได้แถบที่หัวบอกเลขหนึ่ง ท้ายบอกอีกเลขหนึ่ง */
  const proposed = items.filter((it) => it.status === 'proposed');
  const considered = items.length - proposed.length;
  const s = summarise(items);
  const noReason = proposed.filter((it) => !it.reason || it.reason.clauses.length === 0).length;

  return (
    <aside className="simrail" aria-label="ร่างรอบโยกย้าย">
      <div className="rl-hd">
        <b>ร่างของท่าน</b>
        {proposed.length > 0 && <span className="rl-n">{proposed.length} รายการ</span>}
      </div>

      {proposed.length === 0 ? (
        <p className="rl-empty">
          {considered > 0 ? (
            <>
              มีผู้ได้รับการพิจารณา <b>{considered} ท่าน</b> แต่ยังไม่มีใครติดธง &ldquo;เสนอ&rdquo;
              จึงยังไม่นับเข้ายอด — ติดธงได้ที่หน้า <b>เทียบตามเกณฑ์</b>
            </>
          ) : (
            <>
              ยังไม่มีรายการ — กด <b>ใส่บัญชีพิจารณา</b> ที่ผู้อยู่ในข่าย ใส่ได้หลายท่านต่อหนึ่งอัตรา
              แล้วค่อยเทียบตามเกณฑ์ก่อนติดธงเสนอ
            </>
          )}
        </p>
      ) : (
        <ol className="rlrows">
          {proposed.map((it, i) => (
            <li key={it.id} className={it.reason && it.reason.clauses.length > 0 ? undefined : 'is-open'}>
              <div className="rl-top">
                <span className="rl-i">{i + 1}</span>
                <b>{it.person.name}</b>
                <button
                  type="button"
                  className="rl-x"
                  onClick={() => removeFromDraft(it.id)}
                  aria-label={`เอา ${it.person.name} ออกจากร่าง`}
                >
                  ✕
                </button>
              </div>
              <span className="rl-to">ไป {leafUnit(it.target.unit)}</span>
              {it.reason && it.reason.clauses.length > 0 ? (
                <button type="button" className="rl-why" onClick={() => onEditReason(it.id)}>
                  ข้อ 3 ({it.reason.clauses.join(', ')})
                </button>
              ) : (
                /* ⚠ ช่องที่ยังไม่ได้กรอกต้องเห็นว่า "ยังไม่ได้ทำ" ไม่ใช่หายไปเฉย ๆ */
                <button type="button" className="rl-why is-empty" onClick={() => onEditReason(it.id)}>
                  <PlusOutlined aria-hidden="true" /> ระบุเหตุผล
                </button>
              )}
            </li>
          ))}
        </ol>
      )}

      <div className="rl-ft">
        {considered > 0 && (
          <p className="rl-considered">
            อยู่ระหว่างพิจารณาอีก <b>{considered} ท่าน</b> — ยังไม่นับเข้ายอด
          </p>
        )}
        {proposed.length > 0 && (
          <>
            {/* ⚠ เขียนเป็นประโยค ห้ามกลับไปเป็น "สุทธิ N" — ดูเหตุผลที่ netPhrase() */}
            <p className="rl-sum">
              ปิดอัตราว่างเดิม <b>{s.closed}</b> · เปิดใหม่ <b className="is-alert">{s.opened}</b>
              <br />
              <b>{netPhrase(s.net)}</b>
              <span className="rl-src">คำนวณจากข้อมูล ไม่ใช่ AI</span>
            </p>
            {noReason > 0 && (
              <p className="rl-warn">
                ยังไม่ระบุเหตุผล {noReason} รายการ — ออกร่างได้ แต่ในเอกสารจะพิมพ์ว่ายังไม่ได้ระบุ
              </p>
            )}
          </>
        )}
        <button type="button" className="rd-primary rl-b" onClick={onSubmit} disabled={proposed.length === 0}>
          ตรวจร่างแล้วออกเสนอ
        </button>
        <button type="button" className="rd-ghost rl-b" onClick={onOpenMap} disabled={proposed.length === 0}>
          ดูเป็นแผนภาพเต็มจอ
        </button>
      </div>
    </aside>
  );
}

/** ตะกร้าร่าง — รายการที่หยิบไว้ ลูกโซ่ที่ตามมา และข้อควรแก้ */
function DraftPanel({
  items,
  tenureMonths,
  onEditReason,
  onBack,
}: {
  items: DraftItem[];
  tenureMonths: number;
  onEditReason: (id: string) => void;
  onBack: () => void;
}) {
  const s = summarise(items);
  const issues = draftIssues(items, tenureMonths);

  /* ⚠ ลูกโซ่บนแผนภาพ — ใครมารับอัตราที่แต่ละรายการทำให้ว่างลง
     filledBy  : รายการนี้ทำให้ที่เดิมว่าง แล้วลำดับที่เท่าไรมารับต่อ (ไม่มี = ว่างค้าง)
     fillsFrom : อัตราปลายทางของรายการนี้ ว่างเพราะลำดับที่เท่าไรย้ายออก (ไม่มี = ว่างอยู่เดิม)
     คำนวณจากเลขที่ตำแหน่งเท่านั้น ไม่ใช่จากชื่อหน่วยงาน เพราะหน่วยงานเดียวมีหลายอัตราได้ */
  const filledBy = new Map<string, number>();
  const fillsFrom = new Map<string, number>();
  items.forEach((a, ai) => {
    if (!a.person.posNo) return;
    const bi = items.findIndex((b) => b.id !== a.id && b.target.posNo === a.person.posNo);
    if (bi >= 0) {
      filledBy.set(a.id, bi + 1);
      fillsFrom.set(items[bi]!.id, ai + 1);
    }
  });

  if (items.length === 0) {
    return (
      <div className="simmain rd-panel">
        <div className="simempty">
          <RetweetOutlined aria-hidden="true" />
          <b>ร่างยังว่างอยู่</b>
          <p>
            กลับไปเลือกอัตราว่างจากรายการ แล้วกด &ldquo;หยิบใส่ร่าง&rdquo; ที่ผู้อยู่ในข่าย
            หนึ่งร่างใส่ได้หลายรายการ เพราะคำสั่งจริงออกเป็นชุด
          </p>
          <button type="button" className="rd-ghost" onClick={onBack}>กลับไปเลือก</button>
        </div>
      </div>
    );
  }

  return (
    <div className="simmain rd-panel">
      <section className="pmodal-sec">
        <h4>
          แผนภาพการย้าย
          <span className="vc-count">
            {items.length} รายการ · กล่องซ้ายคือหน่วยงานที่จะว่างลง กล่องขวาคือหน่วยงานที่จะได้คน
          </span>
        </h4>

        {/* ⚠ ทำไมถึงเป็นแผนภาพ ไม่ใช่รายการกับหัวข้อ "ผลกระทบต่อเนื่อง" แยกกันแบบเดิม

            แบบเดิมเขียนการย้ายหนึ่งรายการไว้สองที่ — อยู่ในรายการครั้งหนึ่ง
            แล้วเขียนเป็นประโยคซ้ำอีกครั้งข้างล่าง ผู้ใช้ต้องจับคู่เองว่าประโยคไหนคู่กับแถวไหน
            แถมคำอธิบายประโยคเดียวกันยาวสามสิบคำถูกพิมพ์ซ้ำทุกแถว

            แบบนี้ผลที่ตามมาอยู่ในกล่องซ้ายของแถวเดียวกัน ไม่ต้องจับคู่
            และคำถามที่ผู้บริหารถามจริง ๆ คือ "แล้วกองไหนจะไม่มีหัวหน้า" ตอบด้วยสีของกล่องได้ทันที */}
        <ol className="rdmap">
          {items.map((it, i) => {
            const succ = filledBy.get(it.id);
            const pred = fillsFrom.get(it.id);
            return (
              <li key={it.id} className={isLevelDown(it) && !it.consentAttached ? 'is-blocked' : undefined}>
                <div className="rdm-row">
                  {/* ต้นทาง — อัตราที่จะว่างลงเพราะการย้ายรายการนี้
                      ⚠ ถ้ามีคนในร่างเดียวกันมารับต่อ ห้ามเป็นสีแดง เพราะไม่ใช่ปัญหาที่ค้าง */}
                  <div className={`rdm-unit ${succ ? 'is-kept' : 'is-out'}`}>
                    <span className="rdm-cap">ย้ายออกจาก</span>
                    <b>{leafUnit(it.person.unit)}</b>
                    <span className="rdm-sub">
                      {it.person.title}
                      {succ ? ` · ลำดับที่ ${succ} มารับตำแหน่งนี้ต่อ` : ' · จะว่างลง 1 อัตรา'}
                    </span>
                  </div>

                  <div className="rdm-mid">
                    <span className="rdm-n">{i + 1}</span>
                    <b>{it.person.name}</b>
                    <span className="rdm-arr" aria-hidden="true" />
                    {/* ⚠ ช่องเหตุผลที่ยังว่างต้องเห็นว่า "ยังไม่ได้ทำ" ไม่ใช่หายไปเฉย ๆ
                        ถ้าไม่แสดงอะไรเลย ผู้ใช้จะไม่รู้ว่ามีช่องนี้จนกระทั่งตอนกดออกร่าง */}
                    {it.reason && it.reason.clauses.length > 0 ? (
                      <button
                        type="button"
                        className="rdm-why"
                        onClick={() => onEditReason(it.id)}
                        title={it.reason.clauses.map(clauseLabel).join(' · ')}
                      >
                        ข้อ 3 ({it.reason.clauses.join(', ')})
                      </button>
                    ) : (
                      <button type="button" className="rdm-why is-empty" onClick={() => onEditReason(it.id)}>
                        <PlusOutlined aria-hidden="true" /> ระบุเหตุผล
                      </button>
                    )}
                  </div>

                  {/* ปลายทาง — อัตราว่างที่รายการนี้ไปปิด */}
                  <div className="rdm-unit is-in">
                    <span className="rdm-cap">ไปลงตำแหน่งว่างของ</span>
                    <b>{leafUnit(it.target.unit)}</b>
                    <span className="rdm-sub">
                      {it.target.title}
                      {pred
                        ? ` · ปิดอัตราที่ว่างจากลำดับที่ ${pred}`
                        : it.target.vacantMonths !== null
                          ? ` · ปิดอัตราที่ว่างมา ${durationTH(it.target.vacantMonths)}`
                          : ' · ปิดอัตราที่ว่างอยู่เดิม'}
                    </span>
                  </div>

                  <button type="button" className="rdm-x" onClick={() => removeFromDraft(it.id)}>
                    เอาออก
                  </button>
                </div>

                {/* ⚠ เหตุผลที่พิมพ์ไว้ต้องอ่านได้เต็ม ห้ามตัดคำหรือย่อ
                    เพราะเป็นข้อความที่จะถูกพิมพ์ลงเอกสารที่เสนอจริง */}
                {it.reason?.text && (
                  <p className="rdm-text">
                    {it.reason.text}
                    <button type="button" className="rd-link" onClick={() => onEditReason(it.id)}>แก้</button>
                  </p>
                )}

                {isLevelDown(it) && (
                  <div className="rd-block">
                    <WarningOutlined aria-hidden="true" />
                    <span>
                      <b>ย้ายไประดับที่ต่ำกว่าเดิม</b> — กฎ ก.พ. 2564 ข้อ 6 กำหนดว่าทำได้ต่อเมื่อได้รับ
                      ความยินยอมจากผู้ถูกย้าย
                    </span>
                    <label>
                      <input
                        type="checkbox"
                        checked={it.consentAttached}
                        onChange={(e) => setConsent(it.id, e.target.checked)}
                      />
                      แนบหนังสือยินยอมแล้ว
                    </label>
                  </div>
                )}
              </li>
            );
          })}
        </ol>

        {/* ⚠ แถบเดียว ไม่ใช่สองแถบหน้าตาเหมือนกันซ้อนกัน
            และคำอธิบายที่เคยพิมพ์ซ้ำทุกแถว เหลือประโยคเดียวที่นี่
            ขึ้นเฉพาะตอนที่มีอัตราค้างจริง */}
        {(s.unitDelta.length > 0 || s.opened > 0) && (
          <p className="pmodal-note">
            {s.unitDelta.length > 0 && (
              <>
                กำลังคนที่เปลี่ยน —{' '}
                {s.unitDelta
                  .map((u) => `${leafUnit(u.unit)} ${u.delta > 0 ? '+' : ''}${u.delta}`)
                  .join(' · ')}
                <span className="rdm-src">คำนวณจากทะเบียนตำแหน่ง ไม่ใช่ AI</span>
              </>
            )}
            {s.opened > 0 && (
              <em className="rdm-sub">
                อัตราที่ว่างลงจากร่างนี้ ยังหาคนมาแทนจากหน้านี้ไม่ได้ —
                ต้องรอให้ระบบคำนวณผู้เข้าข่ายของอัตราที่เพิ่งว่างก่อน
              </em>
            )}
          </p>
        )}
      </section>

      {issues.length > 0 && (
        <section className="pmodal-sec">
          {/* ⚠ ไม่มีป้ายท้ายหัวข้อแล้ว — ที่มาของแต่ละข้ออยู่ที่ .rd-from ในตัวข้อเอง
              ป้ายรวมท้ายหัวข้อพูดสิ่งเดียวกันซ้ำอีกชั้น */}
          <h4>ข้อควรแก้ก่อนเสนอ</h4>
          <ul className="rdissue">
            {issues.map((is) => (
              <li key={is.id} className={`is-${is.tone}`}>
                <div className="rd-hd">
                  <b>{is.title}</b>
                  <span className="rd-from">{is.source}</span>
                </div>
                <p>{is.detail}</p>
              </li>
            ))}
          </ul>
        </section>
      )}

      <DraftReview items={items} />

      <p className="pmodal-note">
        ร่างนี้เก็บอยู่ในเบราว์เซอร์เครื่องนี้เท่านั้น ยังไม่ได้เสนอ และไม่มีผลกับข้อมูลในระบบ —
        ถ้าจะส่งให้คนอื่นดู ต้องกดออกร่างเป็นไฟล์ก่อน
      </p>
    </div>
  );
}

/**
 * กล่องระบุเหตุผล
 *
 * ────────────────────────────────────────────────────────────────
 *  ⚠ ห้ามบังคับกรอกตอนหยิบ
 * ────────────────────────────────────────────────────────────────
 *
 * ถ้าบังคับ คนจะพิมพ์ "ตามนโยบาย" หรือ "." เพื่อให้ผ่านช่อง
 * ขยะที่หน้าตาเหมือนข้อมูลแย่กว่าช่องว่าง เพราะช่องว่างระบบรู้ว่าไม่มี
 * แต่คำว่า "ตามนโยบาย" จะถูกอ่าน (และถูกผู้ช่วยวิเคราะห์) ว่าเป็นเหตุผลจริง
 *
 * อีกอย่าง ตอนหยิบคนกำลังสำรวจ ยังไม่รู้ด้วยซ้ำว่าจะเอาคนนี้จริงไหม
 * เหตุผลตกผลึกตอนเห็นทั้งชุดแล้ว กล่องนี้จึงไม่เด้งเองอีกต่อไป —
 * หยิบแล้วรายการไปอยู่ในแถบร่างทันที แล้วค่อยกด "ระบุเหตุผล" เมื่อพร้อม
 */
function ReasonModal({ item, onClose }: { item: DraftItem | null; onClose: () => void }) {
  const [clauses, setClauses] = useState<ClauseId[]>(item?.reason?.clauses ?? []);
  const [text, setText] = useState(item?.reason?.text ?? '');

  const hist = useQuery({
    queryKey: ['history', item?.person.personaId],
    queryFn: () => api.history(item!.person.personaId!),
    enabled: Boolean(item?.person.personaId),
    retry: false,
  });

  /**
   * หลักฐานที่ระบบแนบให้เอง — มาจาก work_history ซึ่งเป็นข้อมูลจริง
   * ⚠ ห้ามเอาสมรรถนะ ผลประเมิน หรือคะแนนใด ๆ จาก dump มาใส่ตรงนี้
   *   ค่าพวกนั้นเป็นของสังเคราะห์ (enhancedMeta.generatedFields) ถ้าใส่เข้ามา
   *   มันจะกลายเป็น "หลักฐาน" ในเอกสารที่เสนอผู้บริหาร ทั้งที่ข้างหลังเป็นของปลอม
   */
  const evidence = useMemo(() => {
    const rows = hist.data ?? [];
    if (!item || rows.length === 0) return null;
    const total = rows.reduce((n, r) => n + r.months, 0);
    const line = item.target.line;
    const related = line ? rows.filter((r) => r.title.includes(line)) : [];
    const parts = [`ประวัติการดำรงตำแหน่ง ${rows.length} ตำแหน่ง รวม ${durationTH(total)}`];
    if (related.length > 0) {
      const rm = related.reduce((n, r) => n + r.months, 0);
      parts.push(`ในสายงาน${line} ${related.length} ตำแหน่ง รวม ${durationTH(rm)}`);
    }
    return parts.join(' · ');
  }, [hist.data, item]);

  if (!item) return null;

  const toggle = (id: ClauseId) =>
    setClauses((cur) => (cur.includes(id) ? cur.filter((c) => c !== id) : [...cur, id].sort()));

  const commit = () => {
    setDraftReason(
      item.id,
      clauses.length === 0 && !text.trim() ? null : { clauses, text: text.trim(), evidence },
    );
    onClose();
  };

  return (
    <div className="pmodal-back is-stacked" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="pmodal is-narrow" role="dialog" aria-modal="true" aria-label="ระบุเหตุผล">
        <header className="pmodal-hd">
          <span className="simicon" aria-hidden="true"><RetweetOutlined /></span>
          <div>
            <span className="kind">ระบุเหตุผล</span>
            <b>{item.person.name}</b>
            <span className="sub">
              {leafUnit(item.person.unit)} → {item.target.title} · {leafUnit(item.target.unit)}
            </span>
          </div>
          <button type="button" onClick={onClose} aria-label="ปิดหน้าต่าง">✕</button>
        </header>

        <div className="pmodal-body">
          <section className="pmodal-sec">
            <h4>เข้าเงื่อนไขข้อใด</h4>
            <p className="pmodal-note">
              กฎ ก.พ. ว่าด้วยการย้าย การโอน หรือการเลื่อน พ.ศ. 2564 <b>ข้อ 3</b>
              {' '}กำหนดหกอย่างที่ต้องคำนึงถึง — เลือกได้มากกว่าหนึ่งข้อ
            </p>
            <div className="rdclauses">
              {TRANSFER_CLAUSES.map((c) => (
                <label key={c.id} className={clauses.includes(c.id) ? 'is-on' : undefined}>
                  <input type="checkbox" checked={clauses.includes(c.id)} onChange={() => toggle(c.id)} />
                  <span className="rd-lb">({c.id}) {c.label}</span>
                  {/* ⚠ ป้ายนี้ต้องตรงกับความจริงเสมอ — ดู TRANSFER_CLAUSES ใน lib/rotationDraft.ts */}
                  <em>{c.hasData ? 'ระบบมีหลักฐาน' : 'ไม่มีข้อมูล'}</em>
                </label>
              ))}
            </div>
            <p className="pmodal-note">
              ห้าข้อที่ขึ้นว่า &ldquo;ไม่มีข้อมูล&rdquo; ไม่ได้แปลว่าใช้ไม่ได้ — แปลว่าระบบไม่มีหลักฐานให้
              ต้องแนบเอกสารจากกองการเจ้าหน้าที่เอง
            </p>
          </section>

          <section className="pmodal-sec">
            <h4>เหตุผล</h4>
            <div className="rdquick">
              {QUICK_REASONS.map((r) => (
                <button
                  key={r.text}
                  type="button"
                  onClick={() => {
                    setText((cur) => (cur.trim() ? `${cur.trim()} · ${r.text}` : r.text));
                    setClauses((cur) => (cur.includes(r.clause) ? cur : [...cur, r.clause].sort()));
                  }}
                >
                  {r.text}
                </button>
              ))}
            </div>
            <textarea
              className="rdtext"
              value={text}
              onChange={(e) => setText(e.target.value)}
              rows={3}
              placeholder="เขียนด้วยถ้อยคำของท่านเอง หรือกดปุ่มด้านบนแล้วแก้ต่อ"
              aria-label="เหตุผลในการย้าย"
            />

            {evidence && (
              <div className="rdevidence">
                <span className="rd-hd">หลักฐานที่ระบบแนบให้อัตโนมัติ</span>
                <span className="rd-bd">{evidence}</span>
                <span className="rd-ft">มาจากตาราง work_history ในไฟล์ต้นทาง เป็นข้อมูลจริง ไม่ใช่ค่าที่สร้างขึ้น</span>
              </div>
            )}
          </section>
        </div>

        <div className="rdfoot">
          {/* ⚠ เดิมมีปุ่ม "ข้ามไปก่อน" เพราะกล่องนี้เด้งเองตอนหยิบ
              ตอนนี้กล่องเปิดต่อเมื่อผู้ใช้กดเอง การ "ข้าม" จึงไม่มีความหมาย
              ทางออกของคนที่ยังไม่พร้อมเขียนเหตุผลคือไม่กดเปิดกล่องนี้ตั้งแต่แรก */}
          <span className="rd-sp" />
          <button type="button" className="rd-ghost" onClick={onClose}>ยกเลิก</button>
          <button type="button" className="rd-primary" onClick={commit}>บันทึกเหตุผล</button>
        </div>
      </div>
    </div>
  );
}

/**
 * ถามครั้งเดียวตอนกดออกร่าง
 *
 * ⚠ แยกสองกอง — สีแดงคือเรื่องที่ทำให้คำสั่งเสียเปล่า สีทองคือเรื่องที่ข้ามได้
 *   ถ้าใช้สีเดียวกันทั้งหมด คนจะกดผ่านทุกอันโดยไม่อ่าน
 *   แล้ววันที่เตือนเรื่องจริงก็จะถูกกดผ่านไปด้วย
 */
function SubmitModal({
  items,
  tenureMonths,
  build,
  pdfOverride,
  onFix,
  onClose,
}: {
  items: DraftItem[];
  tenureMonths: number;
  /** ตารางที่จะกลายเป็นไฟล์ Excel — ประกอบตอนกด ไม่ใช่ตอนเรนเดอร์ */
  build: () => ExportTable;
  /** ตัวสร้าง PDF แบบหนังสือเสนอ */
  pdfOverride: () => Promise<void>;
  onFix: (id: string | null) => void;
  onClose: () => void;
}) {
  const issues = draftIssues(items, tenureMonths);
  const blocking = issues.filter((i) => i.tone === 'block');
  const skippable = issues.filter((i) => i.tone !== 'block');

  return (
    <div className="pmodal-back is-stacked" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="pmodal is-narrow" role="dialog" aria-modal="true" aria-label="ออกร่างเสนอ">
        <header className="pmodal-hd">
          <span className="simicon" aria-hidden="true"><RetweetOutlined /></span>
          <div>
            <span className="kind">ออกร่างเสนอ</span>
            <b>
              ร่างนี้มี {items.length} รายการ
              {issues.length > 0 && ` · ยังมี ${issues.length} เรื่องค้าง`}
            </b>
          </div>
          <button type="button" onClick={onClose} aria-label="ปิดหน้าต่าง">✕</button>
        </header>

        <div className="pmodal-body">
          <ul className="rdissue rd-confirm">
            {blocking.map((is) => (
              <li key={is.id} className="is-block">
                <div className="rd-hd"><b>{is.title}</b><span className="rd-from">{is.source}</span></div>
                <p>{is.detail}</p>
                <button type="button" className="rd-danger" onClick={() => onFix(null)}>ไปแก้รายการนี้</button>
              </li>
            ))}
            {skippable.map((is) => (
              <li key={is.id} className={`is-${is.tone}`}>
                <div className="rd-hd"><b>{is.title}</b><span className="rd-from">{is.source}</span></div>
                <p>{is.detail}</p>
              </li>
            ))}
          </ul>

          <p className="pmodal-note">
            {issues.length === 0
              ? 'ไม่มีเรื่องค้าง — กดเลือกรูปแบบไฟล์ด้านล่างได้เลย'
              : `ออกไฟล์ได้ทั้งที่ยังมี ${issues.length} เรื่องค้าง — สิ่งที่เว้นไว้จะพิมพ์ลงในไฟล์ตามจริง ไม่ใช่เว้นบรรทัดว่าง`}
            {' '}การออกไฟล์ไม่เปลี่ยนข้อมูลในระบบ และไม่ใช่คำสั่งแต่งตั้ง
            เป็นการรวมรายการที่เลือกไว้ให้อยู่ในไฟล์เดียว พร้อมเหตุผลและรายการเอกสารที่ยังขาด
          </p>
        </div>

        {/* ⚠ ปุ่มในกล่องนี้ต้อง "ออกไฟล์" ได้จริง
            ของเดิมปุ่มชื่อ "ออกร่างโดยเว้นไว้" เรียก onClose เฉย ๆ คือปิดกล่องแล้วจบ
            ผู้ใช้ที่เพิ่งกดปุ่มชื่อ "ออกร่างเสนอ" มาจึงไม่ได้อะไรเลย และไม่มีอะไรบอกว่าทำไม
            (เจอจากการเดินทั้งเส้นด้วย Playwright 10 ก.ย. 2569)

            ⚠ ถ้อยคำต้องเปลี่ยนตามสถานะจริง ไม่ใช่เขียน "เว้นไว้" ค้างไว้ตลอด
            ร่างที่ไม่มีอะไรค้างแล้วยังขึ้นว่าเว้นไว้ อ่านแล้วเหมือนระบบไม่รู้ว่าตัวเองตรวจอะไรอยู่ */}
        <div className="rdfoot">
          <span className="rd-sp" />
          <button type="button" className="rd-ghost" onClick={() => onFix(null)}>กลับไปแก้ร่าง</button>
          <ExportButtons build={build} pdfOverride={pdfOverride} />
        </div>
      </div>
    </div>
  );
}

/**
 * ตารางที่ส่งออกจากร่าง — หนึ่งแถวต่อหนึ่งการย้าย
 *
 * ⚠ ช่องเหตุผลที่ยังไม่ได้ระบุ ต้องพิมพ์ว่า "ยังไม่ได้ระบุเหตุผล" ไม่ใช่เว้นว่าง
 *   ช่องว่างในเอกสารราชการอ่านได้ว่า "ไม่มีอะไรต้องบอก" ซึ่งคนละความหมายกับ
 *   "ยังไม่ได้กรอก" และคนที่เปิดไฟล์ไม่ใช่คนที่กดออกร่าง
 */
function draftExport(all: DraftItem[], planWarnings: string[]): ExportTable {
  /* ⚠ ไฟล์ที่ออกคือ "ผู้ที่เสนอ" เท่านั้น ผู้ที่อยู่ในบัญชีพิจารณาแต่ยังไม่ถูกเสนอ
     ห้ามลงในตาราง เพราะคนที่เปิดไฟล์จะอ่านว่าเสนอทุกคนในนั้น
     แต่ต้องบอกจำนวนไว้ใน warnings ไม่ใช่หายไปเงียบ ๆ */
  const items = all.filter((it) => it.status === 'proposed');
  const considered = all.length - items.length;
  const s = summarise(items);
  const noReason = items.filter((it) => !it.reason || it.reason.clauses.length === 0).length;

  const warnings = [
    ...planWarnings,
    ...(considered > 0
      ? [
          `บัญชีนี้ยังมีผู้ได้รับการพิจารณาอีก ${considered} ราย ที่ยังไม่ได้ติดธงเสนอ ` +
            'จึงไม่ปรากฏในตารางนี้ — ตารางนี้แสดงเฉพาะผู้ที่เสนอ',
        ]
      : []),
    'เอกสารนี้เป็น "ร่าง" ที่ผู้ใช้เลือกเอง ไม่ใช่ผลการพิจารณาของระบบ ' +
      'และไม่ใช่คำสั่งแต่งตั้ง — ระบบไม่ได้จัดลำดับความเหมาะสมของผู้ใดทั้งสิ้น',
    'กฎ ก.พ. ว่าด้วยการย้าย การโอน หรือการเลื่อนข้าราชการพลเรือนสามัญ พ.ศ. 2564 ข้อ 3 ' +
      'กำหนดให้พิจารณาโดยคำนึงถึงศักยภาพ ผลสัมฤทธิ์ของงาน สมรรถนะ ประวัติการรับราชการ ' +
      'ความรู้ความสามารถ ตลอดจนพฤติกรรมทางจริยธรรมและความประพฤติ ' +
      'ซึ่งมีเพียงข้อประวัติการรับราชการที่ระบบนี้มีหลักฐานให้',
    ...(noReason > 0 ? [`${noReason} รายการในร่างนี้ยังไม่ได้ระบุเหตุผล`] : []),
  ];

  return {
    title: 'ร่างรอบโยกย้ายและสับเปลี่ยนตำแหน่ง',
    subtitle:
      `${items.length} รายการ · ปิดอัตราว่าง ${s.closed} · เปิดอัตราว่างใหม่ ${s.opened} · สุทธิ ${s.net}`,
    warnings,
    columns: [
      { key: 'no', label: 'ลำดับ', width: 7, numeric: true },
      { key: 'name', label: 'ผู้ถูกย้าย', width: 24 },
      { key: 'fromTitle', label: 'ตำแหน่งเดิม', width: 28 },
      { key: 'fromUnit', label: 'หน่วยงานเดิม', width: 32 },
      { key: 'fromMonths', label: 'ครองตำแหน่งเดิม (เดือน)', width: 18, numeric: true },
      { key: 'toTitle', label: 'ตำแหน่งที่จะแต่งตั้ง', width: 28 },
      { key: 'toUnit', label: 'หน่วยงานปลายทาง', width: 32 },
      { key: 'toPosNo', label: 'เลขที่ตำแหน่ง', width: 14 },
      { key: 'vacant', label: 'ว่างมาแล้ว (เดือน)', width: 16, numeric: true },
      { key: 'clauses', label: 'เข้าเงื่อนไขข้อ 3', width: 30 },
      { key: 'reason', label: 'เหตุผล', width: 46 },
      { key: 'evidence', label: 'หลักฐานจากประวัติการดำรงตำแหน่ง', width: 44 },
      { key: 'pending', label: 'เอกสารที่ยังขาด', width: 30 },
    ],
    rows: items.map((it, i) => ({
      no: i + 1,
      name: it.person.name,
      fromTitle: it.person.title,
      fromUnit: it.person.unit,
      fromMonths: it.person.monthsInPosition,
      toTitle: it.target.title,
      toUnit: it.target.unit,
      toPosNo: it.target.posNo,
      vacant: it.target.vacantMonths,
      clauses:
        it.reason && it.reason.clauses.length > 0
          ? it.reason.clauses.map((c) => `(${c}) ${clauseLabel(c)}`).join(' · ')
          : 'ยังไม่ได้ระบุเหตุผล',
      reason: it.reason?.text || (it.reason ? '' : 'ยังไม่ได้ระบุเหตุผล'),
      evidence: it.reason?.evidence ?? '',
      pending: isLevelDown(it) && !it.consentAttached ? 'หนังสือยินยอมตามข้อ 6' : '',
    })),
  };
}

/**
 * ผู้ช่วยอ่านเหตุผล — ส่วนเดียวในหน้านี้ที่เรียกโมเดล
 *
 * ────────────────────────────────────────────────────────────────
 *  เส้นแบ่งที่ห้ามข้าม
 * ────────────────────────────────────────────────────────────────
 *
 * ทุกตัวเลขบนหน้านี้คำนวณจากข้อมูลทั้งหมด (ดู summarise · draftIssues)
 * ผู้ช่วยได้เลขพวกนั้นไป "เล่า" เท่านั้น ห้ามคิดเอง และเซิร์ฟเวอร์ตัดข้อความ
 * ที่มีตัวเลขนอกบัญชีขาวทิ้งก่อนส่งกลับมา (ดู services/rotationReview.ts)
 *
 * ⚠ ปุ่มนี้ต้องกดเอง ไม่ยิงอัตโนมัติตอนเปิดร่าง
 *   เป็นเส้นเดียวในระบบที่มีค่าใช้จ่ายต่อคำขอ และร่างเปลี่ยนทุกครั้งที่หยิบคนเพิ่ม
 *   ถ้ายิงเองจะเสียเงินทุกคลิกโดยที่ผู้ใช้ยังไม่ได้อยากอ่าน
 *
 * ⚠ บรรทัด "ตรวจจาก N ใน M รายการ" ต้องขึ้นก่อนข้อสังเกตเสมอ
 *   ห้ามให้ผู้ใช้เห็นข้อสังเกตสามข้อแล้วเข้าใจว่าตรวจครบทั้งชุด
 */
function DraftReview({ items }: { items: DraftItem[] }) {
  const status = useQuery({ queryKey: ['ask-status'], queryFn: api.askStatus, staleTime: Infinity });
  const review = useMutation({
    mutationFn: () => {
      const s = summarise(items);
      return api.rotationReview(toReviewItems(items), {
        closed: s.closed,
        opened: s.opened,
        net: s.net,
      });
    },
  });

  /* ฟีเจอร์ปิดอยู่ = ไม่ขึ้นปุ่มเลย ดีกว่าขึ้นปุ่มที่กดแล้วได้คำว่า "ทำไม่ได้" */
  if (status.data && !status.data.enabled) return null;

  const r = review.data;

  return (
    <section className="pmodal-sec">
      <h4>ผู้ช่วยอ่านเหตุผล</h4>

      {/* ⚠ นี่คือปุ่มเรียกใช้ ไม่ใช่ย่อหน้าอธิบายอีกอัน
          ของเดิมเป็นแถบสีเดียวกับหมายเหตุอื่นในหน้า พร้อมปุ่มเส้นขอบจาง ๆ
          กวาดตาผ่านแล้วอ่านเป็นคำอธิบายท้ายหน้า ไม่ใช่สิ่งที่กดได้

          ⚠ ประโยคขอบเขต ("ไม่ประเมินความเหมาะสม" ฯลฯ) ห้ามหาย
            เดิมอยู่เป็นป้ายท้ายหัวข้อซึ่งผู้ใช้ขอให้เอาออก จึงย้ายมาอยู่บรรทัดล่างของกล่องนี้แทน */}
      {!r && (
        <div className="rdask">
          <span className="rdask-ic" aria-hidden="true"><ReadOutlined /></span>
          <div className="rdask-tx">
            <b>ให้ผู้ช่วยอ่านเหตุผลที่ท่านเขียนไว้</b>
            <span>
              บอกว่าในชุดเดียวกันมีถ้อยคำที่ขัดกันหรือไม่
              และเรียบเรียงย่อหน้าสำหรับบันทึกเสนอจากถ้อยคำของท่านเอง
            </span>
            <em>ไม่เพิ่มเหตุผลใหม่ ไม่จัดลำดับ ไม่ประเมินความเหมาะสม และไม่คำนวณตัวเลขใด ๆ</em>
          </div>
          <button
            type="button"
            className="rd-primary rdask-go"
            onClick={() => review.mutate()}
            disabled={review.isPending}
          >
            {review.isPending ? 'กำลังอ่าน…' : 'ให้ผู้ช่วยอ่านเหตุผล'}
          </button>
        </div>
      )}

      {review.isError && (
        <p className="state error sm">
          เรียกผู้ช่วยไม่สำเร็จ — {String(review.error)}
        </p>
      )}

      {r && (
        <>
          <div className="rdcover">
            <b>
              ตรวจจาก {r.coverage.withReason} ใน {r.coverage.total} รายการ
            </b>
            {r.coverage.withReason < r.coverage.total && (
              <span>
                อีก {r.coverage.total - r.coverage.withReason} รายการยังไม่ได้ระบุเหตุผล
                จึงยังตรวจความสอดคล้องของทั้งชุดไม่ได้ — ผู้ช่วยไม่เติมเหตุผลให้เอง
              </span>
            )}
          </div>

          {r.warnings.length > 0 && (
            <ul className="rotwarn">
              {r.warnings.map((w) => (
                <li key={w}>{w}</li>
              ))}
            </ul>
          )}

          {r.observations.length > 0 ? (
            <ul className="rdissue">
              {r.observations.map((o) => (
                <li key={o.title} className="is-note">
                  <div className="rd-hd">
                    <b>{o.title}</b>
                    {/* ป้ายที่มา ต้องต่างจาก "คำนวณ" ให้ชัด ผู้อ่านต้องแยกออกว่าอันไหนเป็นความเห็น */}
                    <span className="rd-from is-ai">ผู้ช่วยอ่านเหตุผล</span>
                  </div>
                  <p>{o.detail}</p>
                </li>
              ))}
            </ul>
          ) : (
            <p className="pmodal-note">ผู้ช่วยไม่พบจุดที่ถ้อยคำขัดกันในชุดนี้</p>
          )}

          {r.paragraph && (
            <div className="rddraft">
              <span className="rd-hd">
                ร่างย่อหน้าเหตุผลสำหรับบันทึกเสนอ
                <em>เรียบเรียงจากเหตุผลที่ท่านพิมพ์ ไม่ได้เพิ่มเหตุผลใหม่</em>
              </span>
              <p>{r.paragraph}</p>
              <button
                type="button"
                className="rd-ghost"
                onClick={() => void navigator.clipboard?.writeText(r.paragraph ?? '')}
              >
                คัดลอก
              </button>
            </div>
          )}

          <button type="button" className="rd-link" onClick={() => review.mutate()}>
            อ่านใหม่อีกครั้ง
          </button>
        </>
      )}
    </section>
  );
}
